import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Grid,
  makeStyles,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import {
  Content,
  Header,
  InfoCard,
  Link,
  Page,
} from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { PROJECTS_SAVE_ERROR, useProjectStore } from './useProjectStore';
import { useProjectMembers } from './useProjectMembers';
import { ProjectMembersPanel } from './ProjectMembersPanel';
import { Project, ProjectRole, WorkItemStatus, WorkItemType } from './types';
import { type ProjectsApiOptions } from '../../api/projects';
import { ProjectFilesPanel } from './ProjectFilesPanel';
import { OrgContextSetPanel } from './OrgContextSetPanel';
import { TestCaseTemplateSection } from './TestCaseTemplateSection';
import { ProjectDeleteDialog } from './ProjectDeleteDialog';
import { canPerform } from '../../roles';
import { ProjectRequirementsPanel } from './ProjectRequirementsPanel';

const useStyles = makeStyles((theme: Theme) => ({
  sectionHeader: {
    marginBottom: theme.spacing(2),
  },
  subtle: {
    color: theme.palette.text.secondary,
  },
  tabPanel: {
    marginTop: theme.spacing(3),
  },
  settingsActions: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  caution: {
    color: theme.palette.error.main,
  },
}));

export function countByType(project: Project, type: WorkItemType) {
  return project.workItems.filter(item => item.type === type).length;
}

export function canManageArtifacts(project: Project) {
  return canPerform(project.role, 'project.artifacts.manage');
}

export function canManageProject(project: Project) {
  return canPerform(project.role, 'project.settings');
}

export function canDeleteProject(project: Project) {
  return canPerform(project.role, 'project.delete');
}

const SETTINGS_TAB_INDEX = 4;

const workItemTypeKey: Record<WorkItemType, string> = {
  Requirement: 'requirement',
  'Test Case': 'testCase',
  'Test Script': 'testScript',
};

const workItemStatusKey: Record<WorkItemStatus, string> = {
  Draft: 'draft',
  'In Review': 'inReview',
  Ready: 'ready',
  Blocked: 'blocked',
};

export const ProjectDetailsPage = () => {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const config = useApi(configApiRef);
  const isProjectPreviewMode =
    config.getOptionalBoolean('ses.projectPreviewMode') ?? false;
  const backendUrl = config.getString('backend.baseUrl');
  const projectsProxyPath =
    config.getOptionalString('ses.projectsApi.proxyPath') ??
    '/api/proxy/ses-projects';
  const apiOptions = useMemo<ProjectsApiOptions>(
    () => ({ baseUrl: `${backendUrl}${projectsProxyPath}` }),
    [backendUrl, projectsProxyPath],
  );
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { projectId = '' } = useParams();
  const {
    error,
    loading,
    projects,
    updateProject,
    deleteProject,
    refreshProjects,
  } = useProjectStore();
  const project = projects.find(
    candidate => candidate.id === projectId && !candidate.deleted,
  );

  const membersState = useProjectMembers({
    projectId,
    isPreviewMode: isProjectPreviewMode,
    previewMembers: project?.members,
    savePreviewMembers: members => updateProject(projectId, { members }),
  });

  const transferOwnership = async (userId: string) => {
    await membersState.transferOwnership(userId);
    if (isProjectPreviewMode) {
      await updateProject(projectId, { role: 'Maintainer' });
    } else {
      // Not awaited: the transfer dialog closes when the transfer is done. If
      // the reload shows that this project is no longer listed (the previous
      // Owner can lose access), the page is replaced and the dialog must not
      // still be waiting at that point.
      void refreshProjects({ silent: true });
    }
  };

  const [tab, setTab] = useState(
    searchParams.get('tab') === 'settings' ? SETTINGS_TAB_INDEX : 0,
  );
  const [name, setName] = useState(project?.name ?? '');
  const [description, setDescription] = useState(project?.description ?? '');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(project?.name ?? '');
    setDescription(project?.description ?? '');
  }, [project?.description, project?.name]);

  useEffect(() => {
    if (searchParams.get('tab') === 'settings') {
      setTab(SETTINGS_TAB_INDEX);
    }
  }, [searchParams]);

  const roleLabel = (role: ProjectRole) => t(`roles.${role}`);

  let memberCount: number | string = '–';
  if (membersState.status === 'ready') {
    memberCount = membersState.members.length;
  } else if (isProjectPreviewMode) {
    memberCount = project?.members.length ?? 0;
  }

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const canEditProject = project ? canManageProject(project) : false;
  const canRemoveProject = project ? canDeleteProject(project) : false;
  const hasSettingsChanges = Boolean(
    project &&
      (trimmedName !== project.name ||
        trimmedDescription !== project.description),
  );

  if (loading) {
    return (
      <Page themeId="home">
        <Header title={t('list.title')} />
        <Content>
          <InfoCard title={t('directory.title')}>
            <Typography variant="body2" className={classes.subtle}>
              {t('directory.loading')}
            </Typography>
          </InfoCard>
        </Content>
      </Page>
    );
  }

  if (!project) {
    return (
      <Page themeId="home">
        <Header title={t('notFoundTitle')} />
        <Content>
          <InfoCard title={t('unavailableTitle')}>
            <Typography variant="body2" className={classes.subtle}>
              {t('unavailableDescription')}
            </Typography>
          </InfoCard>
          <Box mt={3}>
            <Link to="/projects">{t('returnToProjects')}</Link>
          </Box>
        </Content>
      </Page>
    );
  }

  const tabKeys = [
    'summary',
    'workItems',
    'context',
    'members',
    'settings',
  ] as const;
  const activeTab = tabKeys.find((_, index) => index === tab);

  return (
    <Page themeId="home">
      <Header title={project.name} subtitle={project.description}>
        <Chip label={roleLabel(project.role)} size="small" />
      </Header>
      <Content>
        <Grid container spacing={3}>
          <Grid item md={3} xs={12}>
            <InfoCard title={t('metrics.requirementsTitle')}>
              <Typography variant="h4">
                {countByType(project, 'Requirement')}
              </Typography>
            </InfoCard>
          </Grid>
          <Grid item md={3} xs={12}>
            <InfoCard title={t('metrics.testCasesTitle')}>
              <Typography variant="h4">
                {countByType(project, 'Test Case')}
              </Typography>
            </InfoCard>
          </Grid>
          <Grid item md={3} xs={12}>
            <InfoCard title={t('metrics.testScriptsTitle')}>
              <Typography variant="h4">
                {countByType(project, 'Test Script')}
              </Typography>
            </InfoCard>
          </Grid>
          <Grid item md={3} xs={12}>
            <InfoCard title={t('metrics.contextArtifactsTitle')}>
              <Typography variant="h4">{project.artifacts.length}</Typography>
            </InfoCard>
          </Grid>
        </Grid>

        <Box mt={3}>
          <PaperTabs
            labels={tabKeys.map(key => t(`tabs.${key}`))}
            value={tab}
            onChange={(_, value) => setTab(value)}
          />
        </Box>

        <div className={classes.tabPanel}>
          {activeTab === 'summary' && (
            <Grid container spacing={3}>
              <Grid item md={8} xs={12}>
                <InfoCard title={t('details.summaryTitle')}>
                  <Typography variant="body1">{project.description}</Typography>
                  <Box mt={2}>
                    <Typography variant="subtitle2">
                      {t('details.aiContextTitle')}
                    </Typography>
                    <Typography variant="body2" className={classes.subtle}>
                      {t('details.aiContextDescription')}
                    </Typography>
                  </Box>
                </InfoCard>
              </Grid>
              <Grid item md={4} xs={12}>
                <InfoCard title={t('details.metaTitle')}>
                  <Typography variant="body2">
                    <strong>{t('labels.organization')}:</strong>{' '}
                    {project.organization}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('labels.role')}:</strong>{' '}
                    {roleLabel(project.role)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('labels.members')}:</strong> {memberCount}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('labels.updated')}:</strong> {project.updatedAt}
                  </Typography>
                </InfoCard>
              </Grid>
            </Grid>
          )}

          {activeTab === 'workItems' && (
            <>
              <ProjectRequirementsPanel
                projectId={project.id}
                isPreviewMode={isProjectPreviewMode}
              />
              <Box mt={3}>
                <InfoCard title={t('metrics.workItemsTitle')}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('table.title')}</TableCell>
                        <TableCell>{t('table.type')}</TableCell>
                        <TableCell>{t('table.status')}</TableCell>
                        <TableCell>{t('table.owner')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {project.workItems.map(item => (
                        <TableRow key={item.id}>
                          <TableCell>{item.title}</TableCell>
                          <TableCell>
                            {t(`workItemTypes.${workItemTypeKey[item.type]}`)}
                          </TableCell>
                          <TableCell>
                            {t(
                              `workItemStatuses.${
                                workItemStatusKey[item.status]
                              }`,
                            )}
                          </TableCell>
                          <TableCell>{item.owner}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </InfoCard>
              </Box>
            </>
          )}

          {activeTab === 'context' && (
            <>
              <ProjectFilesPanel
                project={project}
                apiOptions={apiOptions}
                isPreviewMode={isProjectPreviewMode}
              />
              <Box mt={3}>
                <OrgContextSetPanel
                  project={project}
                  apiOptions={apiOptions}
                  isPreviewMode={isProjectPreviewMode}
                />
              </Box>
            </>
          )}

          {activeTab === 'members' && (
            <ProjectMembersPanel
              projectRole={project.role}
              members={membersState.members}
              status={membersState.status}
              reload={membersState.reload}
              searchUsers={membersState.searchUsers}
              addMember={membersState.addMember}
              assignRole={membersState.assignRole}
              removeMember={membersState.removeMember}
              transferOwnership={transferOwnership}
            />
          )}

          {activeTab === 'settings' && (
            <InfoCard title={t('settings.title')}>
              <Typography variant="body2" className={classes.subtle}>
                {isProjectPreviewMode
                  ? t('settings.previewEnabledDescription')
                  : t('settings.previewDisabledDescription')}
              </Typography>
              {error === PROJECTS_SAVE_ERROR && (
                <Box mt={2}>
                  <Typography variant="body2" className={classes.caution}>
                    {t('settings.saveError')}
                  </Typography>
                </Box>
              )}
              <Box mt={3}>
                <TextField
                  fullWidth
                  error={canEditProject && !trimmedName}
                  helperText={
                    canEditProject && !trimmedName
                      ? t('fields.projectNameRequired')
                      : undefined
                  }
                  label={t('fields.projectName')}
                  value={name}
                  onChange={event => setName(event.target.value)}
                  variant="outlined"
                  disabled={!canEditProject || saving}
                />
              </Box>
              <Box mt={2}>
                <TextField
                  fullWidth
                  label={t('fields.description')}
                  value={description}
                  onChange={event => setDescription(event.target.value)}
                  variant="outlined"
                  multiline
                  minRows={4}
                  disabled={!canEditProject || saving}
                />
              </Box>
              <div className={classes.settingsActions}>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={
                    !canEditProject ||
                    saving ||
                    !trimmedName ||
                    !hasSettingsChanges
                  }
                  onClick={async () => {
                    setSaving(true);
                    try {
                      await updateProject(project.id, {
                        name: trimmedName,
                        description: trimmedDescription,
                      });
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  {saving ? t('actions.saving') : t('actions.saveChanges')}
                </Button>
                <Button
                  className={classes.caution}
                  variant="outlined"
                  disabled={!canRemoveProject}
                  onClick={() => setDeleteOpen(true)}
                >
                  {t('actions.deleteProject')}
                </Button>
              </div>
              <Box mt={4}>
                <TestCaseTemplateSection
                  projectId={project.id}
                  canManage={canManageProject(project)}
                />
              </Box>
            </InfoCard>
          )}
        </div>
      </Content>
      <ProjectDeleteDialog
        project={deleteOpen ? project : null}
        deleteProject={deleteProject}
        error={error}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => navigate('/projects')}
      />
    </Page>
  );
};

function PaperTabs(props: {
  labels: string[];
  value: number;
  onChange: (_: unknown, value: number) => void;
}) {
  return (
    <Tabs
      value={props.value}
      onChange={props.onChange}
      indicatorColor="primary"
      textColor="primary"
      variant="scrollable"
      scrollButtons="auto"
    >
      {props.labels.map(label => (
        <Tab key={label} label={label} />
      ))}
    </Tabs>
  );
}

export const projectDetailsPage = <ProjectDetailsPage />;
